import styled from '@emotion/styled';
import type { FabricEditor } from 'modules/fabric-editor';
import {
  DEFAULT_PLAYER_RANKING,
  PlayerRankingStat,
  type PlayerRankingDirection,
  type PlayerRankingSettings,
} from 'modules/manager/domain/player-stats';
import { TemplateType } from 'modules/manager/domain/template-type';
import { selectActiveTemplate, setPlayerRankingLocal, updatePlayerRanking } from 'modules/manager/store';
import React, { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import CommitOptionalNumberField from '../shared/commit-optional-number-field';
import HelpBubble from '../shared/help-bubble';
import InspectorRow from '../shared/inspector-row';
import InspectorSection from '../shared/inspector-section';
import SelectField from '../shared/select-field';

interface CommonPlayerRankingSectionProps {
  editor: FabricEditor;
}

const STAT_OPTIONS = [
  { value: PlayerRankingStat.GOALS, label: 'Голы' },
  { value: PlayerRankingStat.ASSISTS, label: 'Голевые передачи' },
  { value: PlayerRankingStat.GOALS_ASSISTS, label: 'Голы + передачи' },
  { value: PlayerRankingStat.GAMES, label: 'Игры' },
  { value: PlayerRankingStat.YELLOW_CARDS, label: 'Жёлтые карточки' },
  { value: PlayerRankingStat.RED_CARDS, label: 'Красные карточки' },
];

const DIRECTION_OPTIONS = [
  { value: 'desc', label: 'По убыванию' },
  { value: 'asc', label: 'По возрастанию' },
];

const SECTION_HELP = (
  <span>
    Определяет, кто из игроков чемпионата попадёт на какое место в шаблоне. Первое место («Игрок&nbsp;№&nbsp;1») получает игрок с
    лучшим значением выбранной статистики, второе — следующий и так далее.
    <br />
    <br />
    На то, <i>какие</i> данные показывают плейсхолдеры, это не влияет: шаблон может одновременно показывать и голы, и передачи —
    ранжирование лишь решает порядок игроков.
  </span>
);

/**
 * Which statistic a player-statistics template ranks by. Template-level, like
 * «Раскладка» — the render link only names the championship and the template, so
 * this is the only place the ranking is decided.
 *
 * Replaces the create-time «Показатель» subtype: a template may show several
 * stats at once, so pinning the whole template to one indicator was the wrong
 * axis; only the ranking needs to name one.
 */
const CommonPlayerRankingSection: React.FC<CommonPlayerRankingSectionProps> = ({ editor }) => {
  const dispatch = useAppDispatch();
  const template = useAppSelector(selectActiveTemplate);
  const templateType = editor.placeholders?.getTemplateType();
  const ranking = template?.playerRanking ?? DEFAULT_PLAYER_RANKING;

  const commit = useCallback(
    (next: PlayerRankingSettings) => {
      if (!template) return;
      dispatch(setPlayerRankingLocal(next));
      dispatch(updatePlayerRanking({ id: template.id, playerRanking: next }));
    },
    [dispatch, template],
  );

  if (templateType !== TemplateType.TOP_PLAYERS_BY_INDICATORS || !template) return null;

  return (
    <InspectorSection title="Ранжирование" headerAction={<HelpBubble content={SECTION_HELP} />}>
      <InspectorRow label="Статистика">
        <SelectField
          value={ranking.stat}
          onChange={(v) => commit({ ...ranking, stat: v as PlayerRankingStat })}
          options={STAT_OPTIONS}
        />
      </InspectorRow>
      <InspectorRow label="Порядок">
        <FieldRow>
          <SelectField
            value={ranking.direction}
            onChange={(v) => commit({ ...ranking, direction: v as PlayerRankingDirection })}
            options={DIRECTION_OPTIONS}
          />
          <HelpBubble
            content={
              <span>
                <b>По убыванию</b> — первым идёт игрок с наибольшим значением (лучший бомбардир).
                <br />
                <b>По возрастанию</b> — первым идёт игрок с наименьшим значением.
              </span>
            }
          />
        </FieldRow>
      </InspectorRow>
      <InspectorRow label="Топ N">
        <FieldRow>
          <CommitOptionalNumberField
            value={ranking.limit}
            placeholder="все"
            min={1}
            onCommit={(v) => commit(v === undefined ? omitLimit(ranking) : { ...ranking, limit: v })}
          />
          <HelpBubble
            content={
              <span>
                Сколько игроков рендерить. Пусто — все игроки чемпионата со статистикой.
                <br />
                <br />
                Если задать меньше, чем мест нарисовано в шаблоне, лишние блоки не попадут в картинку — см. «Раскладка игроков».
              </span>
            }
          />
        </FieldRow>
      </InspectorRow>
    </InspectorSection>
  );
};

export default CommonPlayerRankingSection;

// The field is genuinely optional: an absent `limit` means "no limit", which is
// not the same as any number, so clearing it must drop the key rather than store
// a sentinel.
function omitLimit(ranking: PlayerRankingSettings): PlayerRankingSettings {
  const { limit, ...rest } = ranking;
  return rest;
}

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }

  & > :last-child {
    flex: 0 0 auto;
  }
`;
